import { type ReactNode } from 'react';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui';
import { EntityCardsContainer } from './EntityCardsContainer';

type EntityManagementSectionProps = {
	className?: string;
	title: string;
	description: string;
	searchPlaceholder: string;
	searchValue: string;
	onSearchChange: (value: string) => void;
	/** Quando true, o botão secundário (contorno) fica à esquerda do primário (preto). */
	reverseActionButtonsOrder?: boolean;
	primaryActionText: string;
	onPrimaryAction: () => void;
	secondaryActionText: string;
	onSecondaryAction: () => void;
	hasAnyEntities: boolean;
	hasVisibleEntities: boolean;
	emptyInitialTitle: string;
	emptyInitialDescription: string;
	emptyActionText: string;
	onEmptyActionClick: () => void;
	children: ReactNode;
};

export const EntityManagementSection = ({
	className,
	title,
	description,
	searchPlaceholder,
	searchValue,
	onSearchChange,
	reverseActionButtonsOrder = false,
	primaryActionText,
	onPrimaryAction,
	secondaryActionText,
	onSecondaryAction,
	hasAnyEntities,
	hasVisibleEntities,
	emptyInitialTitle,
	emptyInitialDescription,
	emptyActionText,
	onEmptyActionClick,
	children,
}: EntityManagementSectionProps) => {
	return (
		<div className={className ?? 'space-y-6'}>
			<div className="space-y-1">
				<h2 className="text-2xl font-black tracking-tight text-stone-900">
					{title}
				</h2>
				<p className="text-sm text-stone-500">{description}</p>
			</div>

			<div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
				<div className="relative w-full lg:max-w-xl lg:flex-1">
					<Search
						className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500"
						size={18}
					/>
					<input
						type="text"
						placeholder={searchPlaceholder}
						className="w-full rounded-full border-2 border-zinc-100 bg-white py-2.5 pl-10 pr-4 text-sm font-medium text-stone-900 shadow-sm outline-none transition-all placeholder:text-zinc-400 focus:border-stone-900 focus:ring-2 focus:ring-stone-900/10"
						value={searchValue}
						onChange={(e) => onSearchChange(e.target.value)}
					/>
				</div>
				<div className="flex flex-wrap items-center gap-2 lg:shrink-0">
					{reverseActionButtonsOrder ? (
						<>
							<button
								type="button"
								onClick={onSecondaryAction}
								className="inline-flex w-full items-center justify-center gap-1.5 rounded-full border border-stone-200 bg-white px-5 py-2.5 text-sm font-bold text-stone-900 shadow-sm transition-colors hover:bg-stone-50 sm:w-auto"
							>
								<Plus className="size-5 stroke-[2.5] text-stone-900" />
								{secondaryActionText}
							</button>
							<Button
								text={primaryActionText}
								startIcon={
									<Plus className="size-5 stroke-[3] text-white" />
								}
								onClick={onPrimaryAction}
								className="w-full justify-center rounded-full bg-black px-5 py-2.5 font-bold shadow-md hover:bg-neutral-800 sm:w-auto"
							/>
						</>
					) : (
						<>
							<Button
								text={primaryActionText}
								startIcon={
									<Plus className="size-5 stroke-[3] text-white" />
								}
								onClick={onPrimaryAction}
								className="w-full justify-center rounded-full bg-black px-5 py-2.5 font-bold shadow-md hover:bg-neutral-800 sm:w-auto"
							/>
							<button
								type="button"
								onClick={onSecondaryAction}
								className="inline-flex w-full items-center justify-center gap-1.5 rounded-full border border-stone-200 bg-white px-5 py-2.5 text-sm font-bold text-stone-900 shadow-sm transition-colors hover:bg-stone-50 sm:w-auto"
							>
								<Plus className="size-5 stroke-[2.5] text-stone-900" />
								{secondaryActionText}
							</button>
						</>
					)}
				</div>
			</div>

			<EntityCardsContainer
				hasAnyEntities={hasAnyEntities}
				hasVisibleEntities={hasVisibleEntities}
				emptyInitialTitle={emptyInitialTitle}
				emptyInitialDescription={emptyInitialDescription}
				emptyActionText={emptyActionText}
				onEmptyActionClick={onEmptyActionClick}
			>
				{children}
			</EntityCardsContainer>
		</div>
	);
};
